import type { AssetResponse } from 'models/asset';
import type { Product, VideolyVideo } from 'models/product';
import { filterTruthy, withTruthyProps } from 'utils/collection';
import { is } from 'utils/helpers';

interface Video {
	largeImageHeight: number;
	largeImageSrc: string;
	largeImageWidth: number;
	smallImageHeight: number;
	smallImageSrc: string;
	smallImageWidth: number;
	title: string;
	youTubeId: string;
}

function getVideolyThumbnail(video: VideolyVideo, size: 'large' | 'small') {
	const { thumbs } = video;
	const largeToSmall = [
		thumbs.maxres,
		thumbs.standard,
		thumbs.high,
		thumbs.medium,
		thumbs.default,
	];
	const images = size === 'small' ? largeToSmall.toReversed() : largeToSmall;
	return withTruthyProps(
		images.find((img) => img?.url),
		'url',
	);
}

export function getVideos(product: Product): Video[] {
	const videos = (product.videoly?.videoList ?? [])
		.filter((video) => video.type === 'youtube')
		.map((video) => {
			const largeThumb = getVideolyThumbnail(video, 'large');
			const smallThumb = getVideolyThumbnail(video, 'small');
			return {
				title: video.title,
				largeImageSrc: largeThumb?.url,
				largeImageWidth: largeThumb?.width,
				largeImageHeight: largeThumb?.height,
				smallImageSrc: smallThumb?.url,
				smallImageWidth: smallThumb?.width,
				smallImageHeight: smallThumb?.height,
				youTubeId: video.providerVideoId,
			};
		})
		.filter(is.truthy);
	return filterTruthy(
		videos,
		'largeImageSrc',
		'largeImageWidth',
		'largeImageHeight',
		'smallImageSrc',
		'smallImageWidth',
		'smallImageHeight',
		'youTubeId',
	);
}

export function isVideo(
	item: Video | AssetResponse | undefined,
): item is Video {
	return Boolean(item && 'youTubeId' in item && item.youTubeId);
}
